/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */
import React, { useEffect, useRef, useState, RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useTheme } from "../Theme/ThemeProvider";
import { Greeting } from "./Greeting";
import { ChatInput } from "../Chat/ChatInput";
import { MobileGallery } from "../PromptGallery/MobileDefaultPrompts";
import { GalleryMode } from "../PromptGallery/data/galleryTypes";
import { LegalDisclaimers } from "./LegalDisclaimers";
import { RootState } from "../../store/store";
import logger from "../../utils/logger";

interface TabletLayoutProps {
  galleryState: {
    current: { mode: GalleryMode | null; direction: number };
    goToModeWithDirection: (mode: GalleryMode | null, direction: number) => void;
    exitDirectionRef: RefObject<number>;
    isAnimating: boolean;
    setIsAnimating: (value: boolean) => void;
    setExitDirectionRef: RefObject<((direction: number) => void) | null>;
    isFirstAnimationRunning: boolean;
    setIsFirstAnimationRunning: (value: boolean) => void;
  };
  onNavigateToChat?: (message: string, tools?: string[]) => void;
}

export function TabletLayout({ galleryState, onNavigateToChat }: TabletLayoutProps) {
  const { mode, theme } = useTheme();
  const [chatInputExternalValue, setChatInputExternalValue] = useState<string | undefined>(undefined);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const { selectedTool } = useSelector((state: RootState) => state.chatMode);

  // Get accent color from theme
  const accent = theme.replace('dark-', '').replace('light-', '') as 'blue' | 'green' | 'purple' | 'none';

  // Handle tile click from gallery
  const handleTileClick = (prompt: string, tool?: string) => {
    logger.log('[TabletLayout] Tile clicked with prompt:', prompt, 'and tool:', tool);
    setChatInputExternalValue(prompt);
    // Trigger tool selection by dispatching a custom event
    // Always dispatch if tool is defined (including 'none' to clear tools)
    if (tool !== undefined) {
      logger.log('[TabletLayout] Dispatching galleryToolSelection event with tool:', tool);
      window.dispatchEvent(new CustomEvent('galleryToolSelection', { detail: { tool } }));
    }
  };

  // Reset external value after it's been applied
  const handleExternalValueApplied = () => {
    setChatInputExternalValue(undefined);
  };

  // Show indicator only briefly after mode/accent change
  const [showIndicator, setShowIndicator] = useState(false);
  const prevTheme = useRef(theme);
  useEffect(() => {
    if (prevTheme.current !== theme) {
      setShowIndicator(true);
      prevTheme.current = theme;
      const timeout = setTimeout(() => setShowIndicator(false), 4000);
      return () => clearTimeout(timeout);
    }
  }, [theme]);

  // Listen for focus/blur events on textarea to show/hide keyboard
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        setKeyboardVisible(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        setKeyboardVisible(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Responsive horizontal padding strategy: minimize margins early (0→1→2→6) then restore wider spacing progressively.
  // Removed items-center so children (ChatInput, Gallery) can span full available width before shrinking.
  return (
    <>
      {/* Dark vs light mode indicator plus accent (flashes briefly when it changes) */}
      {showIndicator && (
        <div className="absolute top-0 right-0 bg-black/50 text-white p-1 text-xs transition-opacity duration-500 z-30">
          {mode} mode | {accent === 'none' ? 'no' : accent} accent
        </div>
      )}

      {/* Main content area - scrollable, with padding at bottom for fixed gallery + input */}
      <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-[200px]" style={{ marginTop: '10vh' }}>
        {/* Centered greeting */}
          <div className="flex flex-col w-full z-20 mt-6 md:mt-8">
          <Greeting />
        </div>

        {/* Legal disclaimers with Note included for tablet */}
        <div className="w-full mt-4">
          <LegalDisclaimers showNote={true} />
        </div>
      </div>

      {/* Fixed bottom container: Gallery + Chat Input */}
      <div 
        className="fixed left-0 right-0 bg-[var(--main-background)] z-[100] transition-all duration-300"
        style={{
          bottom: keyboardVisible ? '35vh' : '0',
        }}
      >
        {/* Horizontal scrollable gallery - hide when keyboard is visible or translation mode is active */}
        <AnimatePresence mode="wait">
          {!keyboardVisible && selectedTool !== 'translation' && (
            <motion.div
              key="gallery"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="w-full"
            >
              <MobileGallery 
                current={galleryState.current}
                onTileClick={handleTileClick}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat input */}
        <div className="w-full px-4 md:px-8 py-2">
          <ChatInput 
            onNavigateToChat={onNavigateToChat}
            externalValue={chatInputExternalValue}
            onExternalValueApplied={handleExternalValueApplied}
          />
        </div>
      </div>

      {/* Simulated keyboard overlay */}
      {keyboardVisible && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-gray-200 dark:bg-gray-800 z-20 transition-transform duration-300"
          style={{
            height: '35vh',
            transform: keyboardVisible ? 'translateY(0)' : 'translateY(100%)',
          }}
        >
          <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <div className="text-sm mb-2">Virtual Keyboard</div>
              <div className="text-xs opacity-60">(Simulated)</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}